import React, { useEffect, useMemo, useState } from 'react';
import './PhoneHandoff.css';

const POLL_MS = 3000;
// Stop asking after this long; a checkout tab left open overnight shouldn't
// poll forever. Kiosks leave the page on their own idle timer well before.
const POLL_LIMIT_MS = 30 * 60 * 1000;

// The checkout URL a phone should open: this page, minus the params that only
// make sense on the screen showing the QR (a phone that scans a kiosk's code
// is an ordinary checkout, not a second kiosk).
export const phoneCheckoutUrl = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('kiosk');
  url.searchParams.delete('returnUrl');
  return url.toString();
};

// "Finish on your phone": a QR of this same checkout, so the buyer can either
// fill the form here or scan and pay on their own device (their saved cards,
// Apple/Google Pay). The uploaded image URL is unique to this order and rides
// the checkout through to /lob, so polling /order-status with it tells this
// screen when the phone has finished. `paused` is set while THIS device is
// submitting its own order, which would otherwise read as "paid elsewhere".
export default function PhoneHandoff({ nonce, imgUrl, kiosk, paused, onPaidElsewhere }) {
  const [qrFailed, setQrFailed] = useState(false);
  const qrSrc = useMemo(
    () => process.env.REACT_APP_BACKEND_URL + '/qr?d=' + encodeURIComponent(phoneCheckoutUrl()),
    []
  );

  useEffect(() => {
    if (!nonce || !imgUrl || paused) return undefined;
    let stopped = false;
    let timer = null;
    const startedAt = Date.now();

    const poll = async () => {
      if (stopped) return;
      if (Date.now() - startedAt > POLL_LIMIT_MS) return;
      try {
        const res = await fetch(process.env.REACT_APP_BACKEND_URL + '/order-status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nonce, imgUrl }),
        });
        const data = await res.json();
        if (!stopped && data.paid) {
          onPaidElsewhere();
          return;
        }
      } catch (e) {
        // Flaky wifi: the next poll tries again.
      }
      timer = setTimeout(poll, POLL_MS);
    };

    timer = setTimeout(poll, POLL_MS);
    return () => { stopped = true; clearTimeout(timer); };
  }, [nonce, imgUrl, paused, onPaidElsewhere]);

  if (qrFailed) return null;

  return (
    <div className={'phone-handoff' + (kiosk ? ' phone-handoff--kiosk' : '')}>
      <img
        className="phone-handoff-qr"
        src={qrSrc}
        alt="QR code to continue this checkout on your phone"
        onError={() => setQrFailed(true)}
      />
      <div className="phone-handoff-copy">
        <div className="phone-handoff-title">Finish on your phone</div>
        <div className="phone-handoff-text">
          Scan to send this postcard from your own phone, or fill out the form below.
        </div>
      </div>
    </div>
  );
}
